import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listAccounts, listEntries, myAcctPerms, reverseEntry } from "../../api/accounting";
import { listBranches } from "../../api/directory";
import { refNumberFor } from "./refNumber";
import { localName } from "../../lib/localName";

const STATUS_KEYS = ["posted", "draft", "approval_pending", "reversed"];

// Journal data + derived stats + branch-name resolver + client-side search.
export const useJournal = () => {
  const { i18n } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [perms, setPerms] = useState([]);
  const [period, setPeriod] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    const rows = await listEntries(period ? { period } : {});
    setEntries(Array.isArray(rows) ? rows : rows?.items || []);
  }, [period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listAccounts().then((rows) => setAccounts(rows.filter((a) => a.is_leaf && a.active !== false)));
    listBranches().then(setBranches).catch(() => setBranches([]));
    myAcctPerms().then((res) => setPerms(res.perms)).catch(() => setPerms([]));
  }, []);

  const stats = useMemo(() => {
    const s = { total: entries.length };
    STATUS_KEYS.forEach((k) => { s[k] = 0; });
    entries.forEach((e) => { if (STATUS_KEYS.includes(e.status)) s[e.status] += 1; });
    return s;
  }, [entries]);

  // يُعاد الحساب عند تبديل اللغة أيضاً حتى تتغيّر أسماء الفروع فوراً.
  const branchNameById = useMemo(() => {
    const m = {}; branches.forEach((b) => { m[b.id] = localName(b); }); return m;
  }, [branches, i18n.language]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${e.number} ${e.source_ref || ""} ${e.description || ""} ${e.source || ""} ${refNumberFor(e)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [entries, query, statusFilter]);

  return {
    entries: filtered, allEntries: entries, accounts, branches, branchNameById,
    period, setPeriod, query, setQuery, statusFilter, setStatusFilter, stats,
    load, reverse: async (e) => { await reverseEntry(e.id); await load(); },
    canPost: perms.includes("acct_post") || perms.includes("acct_manage"),
    canManage: perms.includes("acct_manage"),
  };
};
